import {
  AgentAsToolInputSchema,
  buildStructuredInputSchemaInfo,
  resolveAgentToolInput,
  type StructuredToolInputBuilder,
} from './agent-tool-input';
import type { AgentToolInvocation } from './agent-tool-invocation';
import {
  getAgentToolParentRunConfigFromDetails,
  getInheritedAgentToolRunConfig,
  mergeAgentToolRunConfig,
} from './agent-tool-run-config';
import { saveAgentToolRunResult } from './agent-tool-run-results';
import { registerAgentToolSourceAgent } from './agent-tool-source-registry';
import { getDefaultModelSettings } from './default-model';
import { ModelBehaviorError, UserError } from './errors';
import type { RunStreamEvent } from './events';
import type { InputGuardrail, OutputGuardrail } from './guardrail';
import { getHandoff, type Handoff } from './handoff';
import type { RunToolApprovalItem } from './items';
import { AgentHooks } from './lifecycle';
import logger from './logger';
import { getAllMcpTools, type MCPServer } from './mcp';
import type { Model, ModelSettings } from './model';
import type { RunResult, StreamedRunResult } from './result';
import type { RunConfig, StreamRunOptions } from './run';
import { Runner } from './run';
import { RunContext } from './run-context';
import { RunState } from './run-state';
import {
  type FunctionTool,
  type FunctionToolResult,
  type Tool,
  type ToolApprovalFunction,
  type ToolCallDetails,
  type ToolEnabledFunction,
  type ToolExecuteArgument,
  type ToolInputParametersStrict,
  tool,
} from './tool';
import {
  type AgentInputItem,
  type Expand,
  type HandoffsOutput,
  type JsonSchemaDefinition,
  type ResolvedAgentOutput,
  type TextOutput,
  type UnknownContext,
} from './types';
import type * as protocol from './types/protocol';
import { combineAbortSignals } from './utils/abort-signals';
import { getOutputText } from './utils/messages';
import { toFunctionToolName } from './utils/tools';
import { isZodObject } from './utils/type-guards';
import type { ZodObjectLike } from './utils/zod-compact';

type AgentToolStreamEvent<TAgent extends Agent<any, any>> = {
  // Raw stream event emitted by the nested agent run.
  event: RunStreamEvent;
  // The agent instance being executed as a tool.
  agent: TAgent;
  // The tool call item that triggered this nested run (when available).
  toolCall?: protocol.FunctionCallItem;
};

type AgentToolResumeContextStrategy = 'merge' | 'replace' | 'preferSerialized';

type AgentToolResumeStateOptions = {
  contextStrategy?: AgentToolResumeContextStrategy;
};

type AgentToolRunOptions<TContext, TAgent extends Agent<TContext, any>> = Omit<
  StreamRunOptions<TContext, TAgent>,
  'stream'
>;

type AgentToolInputParameters = Exclude<ToolInputParametersStrict, undefined>;

type CompletedRunResult<TContext, TAgent extends Agent<TContext, any>> = (
  | RunResult<TContext, TAgent>
  | StreamedRunResult<TContext, TAgent>
) & {
  finalOutput: ResolvedAgentOutput<TAgent['outputType']>;
};

export type CompletedAgentToolInvocationRunResult<
  TContext,
  TAgent extends Agent<TContext, any>,
> = CompletedRunResult<TContext, TAgent> & {
  agentToolInvocation: AgentToolInvocation;
};

type AgentToolInputBuilder<TParameters extends AgentToolInputParameters> =
  StructuredToolInputBuilder<ToolExecuteArgument<TParameters>>;

type AgentToolOptions<
  TContext,
  TAgent extends Agent<TContext, any>,
  TParameters extends AgentToolInputParameters,
> = {
  /**
   * The name of the tool. If not provided, the name of the agent will be used.
   */
  toolName?: string;
  /**
   * The description of the tool, which should indicate what the tool does and when to use it.
   */
  toolDescription?: string;
  /**
   * A function that extracts the output text from the agent. If not provided, the last message
   * from the agent will be used.
   */
  customOutputExtractor?: (
    output: CompletedAgentToolInvocationRunResult<TContext, TAgent>,
  ) => string | Promise<string>;
  /**
   * Whether invoking this tool requires approval, matching the behavior of {@link tool} helpers.
   * When provided as a function it receives the tool arguments and can implement custom approval
   * logic.
   */
  needsApproval?: boolean | ToolApprovalFunction<TParameters>;
  /**
   * The schema used to validate tool input. Defaults to `{ input: string }`.
   */
  parameters?: TParameters;
  /**
   * Builds the nested agent input from structured tool input data.
   */
  inputBuilder?: AgentToolInputBuilder<TParameters>;
  /**
   * Include the full JSON Schema for the structured tool input when invoking the agent.
   */
  includeInputSchema?: boolean;
  /**
   * Run configuration for initializing the internal agent runner.
   */
  runConfig?: Partial<RunConfig>;
  /**
   * Additional run options for the agent (as tool) execution.
   */
  runOptions?: AgentToolRunOptions<TContext, TAgent>;
  /**
   * Controls how context is applied when resuming from serialized run state.
   */
  resumeState?: AgentToolResumeStateOptions;
  /**
   * Determines whether this tool should be exposed to the model for the current run.
   */
  isEnabled?:
    | boolean
    | ((args: {
        runContext: RunContext<TContext>;
        agent: Agent<any, any>;
      }) => boolean | Promise<boolean>);
  /**
   * Optional hook to receive streamed events from the nested agent run.
   */
  onStream?: (event: AgentToolStreamEvent<TAgent>) => void | Promise<void>;
};

type AgentToolOptionsWithDefault<
  TContext,
  TAgent extends Agent<TContext, any>,
> = Omit<
  AgentToolOptions<TContext, TAgent, typeof AgentAsToolInputSchema>,
  'parameters'
> & { parameters?: undefined };

type AgentToolEventName = RunStreamEvent['type'] | '*';
type AgentToolEventHandler<TAgent extends Agent<any, any>> = (
  event: AgentToolStreamEvent<TAgent>,
) => void | Promise<void>;
type AgentTool<
  TContext,
  TAgent extends Agent<TContext, any>,
  TParameters extends AgentToolInputParameters,
> = FunctionTool<TContext, TParameters> & {
  on: (
    name: AgentToolEventName,
    handler: AgentToolEventHandler<TAgent>,
  ) => AgentTool<TContext, TAgent, TParameters>;
};

type AgentToolOptionsWithParameters<
  TContext,
  TAgent extends Agent<TContext, any>,
  TParameters extends AgentToolInputParameters,
> = AgentToolOptions<TContext, TAgent, TParameters> & {
  parameters: TParameters;
};

/**
 * 输出对象的类型。如果未提供，则输出将为字符串。
 * 'text' 是一种特殊类型，表示输出将为字符串。
 *
 * @template T 交接输出的类型。
 */
export type AgentOutputType<T = UnknownContext> =
  | TextOutput
  | ZodObjectLike
  | JsonSchemaDefinition
  | HandoffsOutput<T>;

/**
 * Agent 的配置。
 *
 * @template TContext 上下文对象的类型。
 * @template TOutput 输出对象的类型。
 */
export interface AgentConfiguration<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> {
  name: string;

  /**
   * Agent 的指令。当此 Agent 被调用时，将用作“系统提示词”。
   * 描述 Agent 应该做什么，以及它如何响应。
   *
   * 可以是字符串，也可以是动态生成 Agent 指令的函数。
   * 如果你提供一个函数，它将被调用，并传入运行上下文和 Agent 实例。
   * 它必须返回一个字符串。
   */
  instructions:
    | string
    | ((
        runContext: RunContext<TContext>,
        agent: Agent<TContext, TOutput>,
      ) => Promise<string> | string);

  /**
   * Agent 的描述。当 Agent 用作交接目标时使用，
   * 以便大语言模型（LLM）知道它的功能以及何时调用它。
   */
  handoffDescription: string;

  /**
   * 交接是 Agent 可以委托给的子 Agent。你可以提供一个交接列表，
   * Agent 可以选择在相关时委托给它们。这有助于实现关注点分离和模块化。
   */
  handoffs: (Agent<any, any> | Handoff<any, TOutput>)[];

  /**
   * 当检测到交接 Agent 有多种输出类型时，将启用警告日志。
   */
  handoffOutputTypeWarningEnabled?: boolean;

  /**
   * 调用大语言模型（LLM）时使用的模型实现。
   *
   * 默认情况下，如果未设置，Agent 将使用 getDefaultModel 返回的默认模型
   * （目前为 "gpt-5.4-mini"）。
   */
  model: string | Model;

  /**
   * 配置模型特定的调优参数（例如 temperature、top_p 等）。
   */
  modelSettings: ModelSettings;

  /**
   * Agent 可以使用的工具列表。
   */
  tools: Tool<TContext>[];

  /**
   * Agent 可以使用的 [模型上下文协议（Model Context Protocol）](https://modelcontextprotocol.io/) 服务器列表。
   * 每次 Agent 运行时，它都会将这些服务器提供的工具包含在可用工具列表中。
   *
   * 注意：你需要管理这些服务器的生命周期。具体来说，你必须在将服务器传递给 Agent 之前调用
   * `server.connect()`，并在不再需要服务器时调用 `server.close()`。
   * 考虑使用 `connectMcpServers` 或 `MCPServers` 来在同一个地方管理开启/关闭。
   */
  mcpServers: MCPServer[];

  /**
   * 默认与 Agent 并行运行的检查列表；将 `runInParallel` 设置为 false 可在防护措施完成之前
   * 阻止大语言模型（LLM）/工具调用。仅当 Agent 是调用链中的第一个 Agent 时运行。
   */
  inputGuardrails: InputGuardrail[];

  /**
   * 在 Agent 生成响应之后，对其最终输出运行的检查列表。
   * 仅当 Agent 产生最终输出时运行。
   */
  outputGuardrails: OutputGuardrail<TOutput, TContext>[];

  /**
   * 输出对象的类型。如果未提供，则输出将为字符串。
   */
  outputType: TOutput;

  /**
   * 这让你可以配置如何处理工具使用。
   * - run_llm_again：默认行为。工具运行后，大语言模型（LLM）接收结果并做出响应。
   * - stop_on_first_tool：第一个工具调用的输出被用作最终输出。这意味着
   *   大语言模型（LLM）不会处理工具调用的结果。
   * - 一个工具名称列表：如果列表中任何工具被调用，Agent 将停止运行。
   *   最终输出将是第一个匹配的工具调用的输出。大语言模型（LLM）不处理工具调用的结果。
   * - 一个函数：如果你传入一个函数，它将被调用，并传入运行上下文和工具结果列表。
   *   它必须返回一个 `ToolsToFinalOutputResult`，该结果决定工具调用是否产生最终输出。
   *
   * 注意：此配置特定于 `FunctionTools`。托管工具，如文件搜索、网页搜索等，
   * 始终由大语言模型（LLM）处理。
   */
  toolUseBehavior: ToolUseBehavior;

  /**
   * 是否在调用工具后将工具选择重置为默认值。默认为 `true`。
   * 这确保 Agent 不会陷入无限循环的工具使用中。
   */
  resetToolChoice: boolean;
}

export type AgentOptions<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> = Expand<
  Pick<AgentConfiguration<TContext, TOutput>, 'name'> &
    Partial<AgentConfiguration<TContext, TOutput>>
>;

/**
 * An agent is an AI model configured with instructions, tools, guardrails, handoffs and more.
 *
 * We strongly recommend passing `instructions`, which is the "system prompt" for the agent. In
 * addition, you can pass `handoffDescription`, which is a human-readable description of the
 * agent, used when the agent is used inside tools/handoffs.
 *
 * Agents are generic on the context type. The context is a (mutable) object you create. It is
 * passed to tool functions, handoffs, guardrails, etc.
 */
// --- Type utilities for inferring output type from handoffs ---
type ExtractAgentOutput<T> = T extends Agent<any, infer O> ? O : never;
type ExtractHandoffOutput<T> = T extends Handoff<any, infer O> ? O : never;
export type HandoffsOutputUnion<
  Handoffs extends readonly (Agent<any, any> | Handoff<any, any>)[],
> =
  | ExtractAgentOutput<Handoffs[number]>
  | ExtractHandoffOutput<Handoffs[number]>;

/**
 * Helper type for config with handoffs
 *
 * @template TOutput The type of the output object.
 * @template Handoffs The type of the handoffs.
 */
export type AgentConfigWithHandoffs<
  TOutput extends AgentOutputType,
  Handoffs extends readonly (Agent<any, any> | Handoff<any, any>)[],
> = { name: string; handoffs?: Handoffs; outputType?: TOutput } & Partial<
  Omit<
    AgentConfiguration<UnknownContext, TOutput | HandoffsOutputUnion<Handoffs>>,
    'name' | 'handoffs' | 'outputType'
  >
>;

/**
表示一个 AI Agent的类，可使用指令、工具、护栏（guardrails）、转接（handoffs）等进行配置。
强烈建议传入 instructions，它相当于代理的"系统提示词"。此外，你还可以传入
handoffDescription，它是代理的可读描述，当代理在工具/转接中被使用时，会用到该描述。
代理在上下文类型上是泛型的。上下文是你创建的一个（可变）对象。它会被传递给工具函数、
转接、护栏等
 */
export class Agent<
    TContext = UnknownContext,
    TOutput extends AgentOutputType = TextOutput,
  >
  extends AgentHooks<TContext, TOutput>
  implements AgentConfiguration<TContext, TOutput>
{
  /**
   * 创建一个带有转接功能的 Agent，并根据转接代理的输出类型自动推断 TOutput 的联合类型
   */
  static create<
    TOutput extends AgentOutputType = TextOutput,
    Handoffs extends readonly (Agent<any, any> | Handoff<any, any>)[] = [],
  >(
    config: AgentConfigWithHandoffs<TOutput, Handoffs>,
  ): Agent<UnknownContext, TOutput | HandoffsOutputUnion<Handoffs>> {
    return new Agent<UnknownContext, TOutput | HandoffsOutputUnion<Handoffs>>({
      ...config,
      handoffs: config.handoffs as any,
      outputType: config.outputType,
      handoffOutputTypeWarningEnabled: false,
    });
  }

  static DEFAULT_MODEL_PLACEHOLDER = '';

  name: string;
  instructions:
    | string
    | ((
        runContext: RunContext<TContext>,
        agent: Agent<TContext, TOutput>,
      ) => Promise<string> | string);
  handoffDescription: string;
  handoffs: (Agent<any, TOutput> | Handoff<any, TOutput>)[];
  model: string | Model;
  modelSettings: ModelSettings;
  tools: Tool<TContext>[];
  mcpServers: MCPServer[];
  inputGuardrails: InputGuardrail[];
  outputGuardrails: OutputGuardrail<AgentOutputType, TContext>[];
  outputType: TOutput = 'text' as TOutput;
  toolUseBehavior: ToolUseBehavior;
  resetToolChoice: boolean;
  private readonly _toolsExplicitlyConfigured: boolean;
  private readonly _modelSettingsExplicitlyConfigured: boolean;

  constructor(config: AgentOptions<TContext, TOutput>) {
    super();
    if (typeof config.name !== 'string' || config.name.trim() === '') {
      throw new UserError('Agent must have a name.');
    }
    this.name = config.name;
    this.instructions = config.instructions ?? Agent.DEFAULT_MODEL_PLACEHOLDER;
    this.handoffDescription = config.handoffDescription ?? '';
    this.handoffs = config.handoffs ?? [];
    this.model = config.model ?? '';
    this._modelSettingsExplicitlyConfigured =
      config.modelSettings !== undefined;
    this.modelSettings =
      config.modelSettings ??
      getInitialModelSettingsForAgentModel(config.model);
    this.tools = config.tools ?? [];
    this._toolsExplicitlyConfigured = config.tools !== undefined;
    this.mcpServers = config.mcpServers ?? [];
    this.inputGuardrails = config.inputGuardrails ?? [];
    this.outputGuardrails = config.outputGuardrails ?? [];
    if (config.outputType) {
      this.outputType = config.outputType;
    }
    this.toolUseBehavior = config.toolUseBehavior ?? 'run_llm_again';
    this.resetToolChoice = config.resetToolChoice ?? true;
    //
    //     if (
    //       // 用户设置了非默认模型
    //       config.model !== undefined &&
    //       config.model !== Agent.DEFAULT_MODEL_PLACEHOLDER &&
    //       // 默认模型是 gpt-5
    //       isGpt5Default() &&
    //       // 但是，指定的模型不是 gpt-5 模型
    //       (typeof config.model !== "string" ||
    //         !gpt5ReasoningSettingsRequired(config.model)) &&
    //       // 该模型的设置没有被自定义过
    //       config.modelSettings === undefined
    //     ) {
    //       // 在这种情况下，我们应该使用通用的模型设置，
    //       // 因为非 gpt-5 模型不兼容默认的 gpt-5 模型设置。
    //       // 这是为了让代理能够使用非 gpt-5 模型所做的一种尽力而为的尝试。
    //       this.modelSettings = {};
    //     }
    //
    //     // --- Runtime warning for handoff output type compatibility ---
    //     if (
    //       config.handoffOutputTypeWarningEnabled === undefined ||
    //       config.handoffOutputTypeWarningEnabled
    //     ) {
    //       if (this.handoffs && this.outputType) {
    //         const outputTypes = new Set<string>([JSON.stringify(this.outputType)]);
    //         for (const h of this.handoffs) {
    //           if ("outputType" in h && h.outputType) {
    //             outputTypes.add(JSON.stringify(h.outputType));
    //           } else if ("agent" in h && h.agent.outputType) {
    //             outputTypes.add(JSON.stringify(h.agent.outputType));
    //           }
    //         }
    //         if (outputTypes.size > 1) {
    //           logger.warn(
    //             `[Agent] Warning: Handoff agents have different output types: ${Array.from(outputTypes).join(", ")}. You can make it type-safe by using Agent.create({ ... }) method instead.`,
    //           );
    //         }
    //       }
    //     }
  }
  //
  /** @internal */
  hasExplicitModelSettings(): boolean {
    return this._modelSettingsExplicitlyConfigured;
  }

  /**
   * Output schema name.
   */
  get outputSchemaName(): string {
    if (this.outputType === 'text') {
      return 'text';
    } else if (isZodObject(this.outputType)) {
      return 'ZodOutput';
    } else if (typeof this.outputType === 'object') {
      return this.outputType.name;
    }

    throw new Error(`Unknown output type: ${this.outputType}`);
  }

  /**
   * 创建代理的副本，并修改指定的参数。例如，你可以这样做：
   *
   * ```
   * const newAgent = agent.clone({ instructions: 'New instructions' })
   * ```
   *
   * @param config - 要修改的部分配置。
   * @returns 应用了指定修改的新代理。
   */
  clone(
    config: Partial<AgentConfiguration<TContext, TOutput>>,
  ): Agent<TContext, TOutput> {
    const modelSettings =
      'modelSettings' in config
        ? config.modelSettings
        : this.hasExplicitModelSettings()
          ? this.modelSettings
          : undefined;

    return new Agent({
      ...this,
      ...config,
      modelSettings,
    });
  }

  /**
  * 将此Agent转换为一个工具，使其可以被其他Agent调用。

  * 这与 handoffs 有两处不同：

  * 在handoffs 中，新代理会接收对话历史。而在这个工具中，新代理接收的是生成的输入。
  * 在handoffs 中，新代理接管整个对话。而在这个工具中，新代理是作为工具被调用的，对话仍由原始代理继续。
  @param options - 工具的选项。
  @returns 一个运行该代理并返回输出文本的工具。
     */
  asTool<TAgent extends Agent<TContext, TOutput> = Agent<TContext, TOutput>>(
    this: TAgent,
    options: AgentToolOptionsWithDefault<TContext, TAgent>,
  ): AgentTool<TContext, TAgent, typeof AgentAsToolInputSchema>;
  asTool<
    TAgent extends Agent<TContext, TOutput> = Agent<TContext, TOutput>,
    TParameters extends
      AgentToolInputParameters = typeof AgentAsToolInputSchema,
  >(
    this: TAgent,
    options: AgentToolOptionsWithParameters<TContext, TAgent, TParameters>,
  ): AgentTool<TContext, TAgent, TParameters>;
  asTool<
    TAgent extends Agent<TContext, TOutput> = Agent<TContext, TOutput>,
    TParameters extends
      AgentToolInputParameters = typeof AgentAsToolInputSchema,
  >(
    this: TAgent,
    options: AgentToolOptions<TContext, TAgent, TParameters>,
  ): AgentTool<TContext, TAgent, TParameters> {
    const {
      toolName,
      toolDescription,
      customOutputExtractor,
      needsApproval,
      parameters,
      inputBuilder,
      includeInputSchema,
      runConfig,
      runOptions,
      resumeState,
      isEnabled,
      onStream,
    } = options;
    // 事件处理器的作用域限于此代理工具实例，且不共享；我们仅支持注册（不支持移除），以保持 API 简洁。
    const eventHandlers = new Map<
      AgentToolEventName,
      Set<AgentToolEventHandler<TAgent>>
    >();
    const emitEvent = async (event: AgentToolStreamEvent<TAgent>) => {
      // 我们有意保持只增（不删）的语义，以减少 API 面积；处理器的作用域限于此代理工具实例。
      const specific = eventHandlers.get(event.event.type);
      const wildcard = eventHandlers.get('*');
      const candidates = [
        ...(onStream ? [onStream] : []),
        ...(specific ? Array.from(specific) : []),
        ...(wildcard ? Array.from(wildcard) : []),
      ];
      // 并行执行所有处理器，这样慢的 onStream 回调不会阻塞 on(...) 处理器（反之亦然）。
      await Promise.allSettled(
        candidates.map((handler) =>
          Promise.resolve().then(() => handler(event)),
        ),
      );
    };
    const resolvedToolName = toolName ?? toFunctionToolName(this.name);
    const toolParameters = (parameters ??
      AgentAsToolInputSchema) as ToolInputParametersStrict;
    const hasCustomParameters = typeof parameters !== 'undefined';
    const includeSchema = includeInputSchema === true && hasCustomParameters;
    const shouldCaptureToolInput =
      hasCustomParameters ||
      includeSchema ||
      typeof inputBuilder === 'function';
    const schemaInfo = shouldCaptureToolInput
      ? buildStructuredInputSchemaInfo(
          toolParameters,
          resolvedToolName,
          includeSchema,
        )
      : undefined;
    const baseTool = tool<ToolInputParametersStrict, TContext, string>({
      name: resolvedToolName,
      description: toolDescription ?? '',
      parameters: toolParameters,
      strict: true,
      needsApproval: needsApproval as
        | boolean
        | ToolApprovalFunction<ToolInputParametersStrict>,
      isEnabled,
      execute: async (
        params: ToolExecuteArgument<ToolInputParametersStrict>,
        context?: RunContext<TContext>,
        details?: ToolCallDetails,
      ) => {
        const typedParams = params as ToolExecuteArgument<TParameters>;
        const runContextBase: RunContext<TContext> =
          runOptions?.context instanceof RunContext
            ? runOptions.context
            : typeof runOptions?.context !== 'undefined'
              ? new RunContext(runOptions.context)
              : context instanceof RunContext
                ? context
                : typeof context !== 'undefined'
                  ? new RunContext(context as TContext)
                  : new RunContext<TContext>();
        const agentToolInvocation: AgentToolInvocation = {
          toolName: details?.toolCall?.name ?? baseTool.name,
          toolCallId: details?.toolCall?.callId,
          toolArguments: details?.toolCall?.arguments,
        };
        const shouldClearToolInput =
          !shouldCaptureToolInput &&
          typeof runContextBase.toolInput !== 'undefined';
        const runContext: RunContext<TContext> =
          shouldCaptureToolInput &&
          typeof runContextBase._forkWithToolInput === 'function'
            ? runContextBase._forkWithToolInput(typedParams)
            : shouldClearToolInput &&
                typeof runContextBase._forkWithoutToolInput === 'function'
              ? runContextBase._forkWithoutToolInput()
              : runContextBase;
        const resolvedInput = await resolveAgentToolInput({
          params: typedParams,
          schemaInfo,
          inputBuilder,
        });
        if (
          typeof resolvedInput !== 'string' &&
          !Array.isArray(resolvedInput)
        ) {
          throw new ModelBehaviorError('Agent tool called with invalid input');
        }
        const inheritedRunConfig = getInheritedAgentToolRunConfig(
          getAgentToolParentRunConfigFromDetails(details),
          runConfig,
        );
        const nestedRunConfig = mergeAgentToolRunConfig(
          inheritedRunConfig,
          runConfig,
        );
        const runner = new Runner(nestedRunConfig);
        const resumeContextStrategy = resumeState?.contextStrategy ?? 'merge';
        const resumeContext =
          resumeContextStrategy === 'preferSerialized' ? undefined : runContext;
        let runInput: string | AgentInputItem[] | RunState<TContext, TAgent> =
          resolvedInput;
        if (details?.resumeState) {
          if (resumeContextStrategy === 'preferSerialized' || !resumeContext) {
            runInput = await RunState.fromString<TContext, TAgent>(
              this,
              details.resumeState,
            );
          } else {
            if (
              resumeContextStrategy === 'merge' &&
              context &&
              resumeContext !== context
            ) {
              resumeContext._mergeApprovals(context.toJSON().approvals);
            }
            runInput = await RunState.fromStringWithContext<TContext, TAgent>(
              this,
              details.resumeState,
              resumeContext,
              {
                contextStrategy:
                  resumeContextStrategy === 'replace' ? 'replace' : 'merge',
              },
            );
          }
        }
        // 仅当提供了处理器时才切换到流模式，以避免给不需要事件的调用者带来额外开销。
        // 如果提供了旧的 onStream 回调或者注册了事件处理器，则切换到流模式；否则保持非流路径，以避免额外开销。
        const shouldStream =
          typeof onStream === 'function' || eventHandlers.size > 0;
        const configuredSignal = runOptions?.signal;
        const toolCallSignal = details?.signal;
        const { signal: combinedSignal, cleanup: cleanupSignalListeners } =
          configuredSignal && toolCallSignal
            ? combineAbortSignals(configuredSignal, toolCallSignal)
            : {
                signal: toolCallSignal ?? configuredSignal,
                cleanup: () => {},
              };
        const runOptionsWithContext = {
          ...(runOptions ?? {}),
          context: runContext,
          ...(combinedSignal ? { signal: combinedSignal } : {}),
        };
        try {
          const result = shouldStream
            ? await runner.run(this, runInput, {
                ...runOptionsWithContext,
                stream: true,
              })
            : await runner.run(this, runInput, {
                ...runOptionsWithContext,
              });
          const streamPayload = {
            agent: this,
            toolCall: details?.toolCall,
          };

          if (shouldStream) {
            // 通过 unknown 进行转换：异步迭代器的结构是匹配的，我们需要排空流以触发副作用，同时保持公共 API 的稳定。
            const streamResult = result as unknown as StreamedRunResult<
              TContext,
              Agent<TContext, AgentOutputType>
            >;
            // 排空流，将每一个事件传递给已注册的处理器；确保等待完成，以便嵌套运行在返回之前结束。
            for await (const event of streamResult) {
              await emitEvent({
                event,
                ...streamPayload,
              });
            }
            await streamResult.completed;
          }

          const completedResult = result as CompletedRunResult<
            TContext,
            TAgent
          >;
          if (completedResult.state instanceof RunState) {
            completedResult.state._agentToolInvocation = agentToolInvocation;
          }
          const completedResultWithAgentToolInvocation =
            completedResult as CompletedAgentToolInvocationRunResult<
              TContext,
              TAgent
            >;

          const usesStopAtToolNames =
            typeof this.toolUseBehavior === 'object' &&
            this.toolUseBehavior !== null &&
            'stopAtToolNames' in this.toolUseBehavior;

          if (
            typeof customOutputExtractor !== 'function' &&
            usesStopAtToolNames
          ) {
            logger.debug(
              `You're passing the agent (name: ${this.name}) with toolUseBehavior.stopAtToolNames configured as a tool to a different agent; this may not work as you expect. You may want to have a wrapper function tool to consistently return the final output.`,
            );
          }
          let outputText: string;
          if (typeof customOutputExtractor === 'function') {
            outputText = await customOutputExtractor(
              completedResultWithAgentToolInvocation,
            );
          } else {
            const finalOutputText =
              typeof completedResult.finalOutput !== 'undefined'
                ? this.outputType === 'text'
                  ? String(completedResult.finalOutput)
                  : JSON.stringify(completedResult.finalOutput)
                : undefined;
            const rawResponses = completedResult.rawResponses;
            const rawOutputText =
              rawResponses && rawResponses.length > 0
                ? getOutputText(rawResponses[rawResponses.length - 1]!)
                : undefined;
            const normalizedRawOutputText =
              typeof rawOutputText === 'string' && rawOutputText.trim() === ''
                ? undefined
                : rawOutputText;
            const prefersFinalOutput =
              completedResult.state?._finalOutputSource === 'error_handler';
            outputText = prefersFinalOutput
              ? (finalOutputText ?? normalizedRawOutputText ?? '')
              : (normalizedRawOutputText ?? finalOutputText ?? '');
          }

          if (details?.toolCall) {
            saveAgentToolRunResult(
              details.toolCall,
              completedResultWithAgentToolInvocation,
            );
          }
          return outputText;
        } finally {
          cleanupSignalListeners();
        }
      },
    });

    const agentTool: AgentTool<TContext, TAgent, TParameters> = {
      ...baseTool,
      on: (name, handler) => {
        const set =
          eventHandlers.get(name) ?? new Set<AgentToolEventHandler<TAgent>>();
        set.add(handler);
        eventHandlers.set(name, set);
        return agentTool;
      },
    };
    registerAgentToolSourceAgent(agentTool, this);

    return agentTool;
  }

  /**
  * 返回 Agent 的系统提示词

  * 如果代理的 instructions 是一个函数，则会使用 runContext 和代理实例来调用该函数。
     */
  async getSystemPrompt(
    runContext: RunContext<TContext>,
  ): Promise<string | undefined> {
    if (typeof this.instructions === 'function') {
      return await this.instructions(runContext, this);
    }

    return this.instructions;
  }
  //
  //   /**
  // /**
  //
  // 返回代理的提示词模板（如果已定义）。
  //
  // 如果代理的 prompt 是一个函数，则会使用 runContext 和代理实例来调用该函数。
  //    */
  //   async getPrompt(
  //     runContext: RunContext<TContext>,
  //   ): Promise<Prompt | undefined> {
  //     if (typeof this.prompt === "function") {
  //       return await this.prompt(runContext, this);
  //     }
  //     return this.prompt;
  //   }

  /**
  从 MCP 服务器获取可用的工具。

  @returns 由 MCP 驱动的工具
     */
  async getMcpTools(
    runContext: RunContext<TContext>,
  ): Promise<Tool<TContext>[]> {
    if (this.mcpServers.length > 0) {
      return getAllMcpTools({
        mcpServers: this.mcpServers,
        runContext,
        agent: this,
        convertSchemasToStrict: false,
      });
    }

    return [];
  }

  /**
  获取代理的所有工具，包括 MCP 工具和函数工具。

  @returns 所有已配置的工具
     */
  async getAllTools(
    runContext: RunContext<TContext>,
  ): Promise<Tool<TContext>[]> {
    const mcpTools = await this.getMcpTools(runContext);
    const enabledTools: Tool<TContext>[] = [];

    for (const candidate of this.tools) {
      if (candidate.type === 'function') {
        const maybeIsEnabled = (
          candidate as { isEnabled?: ToolEnabledFunction<TContext> | boolean }
        ).isEnabled;

        const enabled =
          typeof maybeIsEnabled === 'function'
            ? await maybeIsEnabled(runContext, this)
            : typeof maybeIsEnabled === 'boolean'
              ? maybeIsEnabled
              : true;
        if (!enabled) {
          continue;
        }
      }
      enabledTools.push(candidate);
    }

    return [...mcpTools, ...enabledTools];
  }

  hasExplicitToolConfig(): boolean {
    return this._toolsExplicitlyConfigured;
  }

  /**
  返回在当前运行中应当暴露给模型的转接项。

  那些提供了 isEnabled 函数并返回 false 的转接项会被忽略。
     */
  async getEnabledHandoffs(
    runContext: RunContext<TContext>,
  ): Promise<Handoff<any, any>[]> {
    const handoffs = this.handoffs?.map((h) => getHandoff(h)) ?? [];
    const enabled: Handoff<any, any>[] = [];
    for (const handoff of handoffs) {
      if (await handoff.isEnabled({ runContext, agent: this })) {
        enabled.push(handoff);
      }
    }
    return enabled;
  }

  /**
  处理代理的最终输出。

  @param output - 代理的输出。

  @returns 解析后的输出。
     */
  processFinalOutput(output: string): ResolvedAgentOutput<TOutput> {
    if (this.outputType === 'text') {
      return output as ResolvedAgentOutput<TOutput>;
    }

    if (typeof this.outputType === 'object') {
      const parsed = JSON.parse(output);

      if (isZodObject(this.outputType)) {
        return this.outputType.parse(parsed) as ResolvedAgentOutput<TOutput>;
      }

      return parsed as ResolvedAgentOutput<TOutput>;
    }

    throw new Error(`Unknown output type: ${this.outputType}`);
  }

  /**
  返回代理的 JSON 表示，该表示是可序列化的。

  @returns 一个包含代理名称的 JSON 对象。
     */
  toJSON() {
    return {
      name: this.name,
    };
  }
}

export type ToolUseBehaviorFlags = 'run_llm_again' | 'stop_on_first_tool';

export type ToolsToFinalOutputResult =
  | {
      /**
       * 是否为最终输出。如果为 `false`，大语言模型将再次运行并接收工具调用输出。
       */
      isFinalOutput: false;
      /**
       * 代理是否被工具审批中断。如果为 `true`，大语言模型将再次运行并接收工具调用输出。
       */
      isInterrupted: undefined;
    }
  | {
      isFinalOutput: false;
      /**
       * 代理是否被工具审批中断。如果为 `true`，大语言模型将再次运行并接收工具调用输出。
       */
      isInterrupted: true;
      interruptions: RunToolApprovalItem[];
    }
  | {
      /**
       * 是否为最终输出。如果为 `false`，大语言模型将再次运行并接收工具调用输出。
       */
      isFinalOutput: true;

      /**
       * 代理是否被工具审批中断。如果为 `true`，大语言模型将再次运行并接收工具调用输出。
       */
      isInterrupted: undefined;

      /**
       * 最终输出。如果 `isFinalOutput` 为 `false`，则可以为 undefined；
       * 否则必须是一个字符串，将根据代理的 `outputType` 进行处理。
       */
      finalOutput: string;
    };

/**
 * 一个函数，接收运行上下文和工具结果列表，并返回一个 `ToolsToFinalOutputResult`。
 */
export type ToolToFinalOutputFunction = (
  context: RunContext,
  toolResults: FunctionToolResult[],
) => ToolsToFinalOutputResult | Promise<ToolsToFinalOutputResult>;

/**
 * 工具被调用时代理的行为。
 */
export type ToolUseBehavior =
  | ToolUseBehaviorFlags
  | {
      /**
       * 会阻止代理继续运行的工具名称列表。最终输出将是
       * 列表中第一个被调用的工具的输出。
       */
      stopAtToolNames: string[];
    }
  | ToolToFinalOutputFunction;

function getInitialModelSettingsForAgentModel(
  model: string | Model | undefined,
): ModelSettings {
  if (model === undefined || model === Agent.DEFAULT_MODEL_PLACEHOLDER) {
    return getDefaultModelSettings();
  }
  if (typeof model === 'string' && model !== Agent.DEFAULT_MODEL_PLACEHOLDER) {
    return getDefaultModelSettings(model);
  }
  return {};
}
